import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Container, Spinner } from 'react-bootstrap';
import { FaCheckCircle, FaExclamationTriangle, FaLink, FaShieldAlt } from 'react-icons/fa';
import { useParams } from 'react-router-dom';
import employeeBadgeService from '../services/employeeBadgeService';
import buildPublicAssetUrl from '../utils/publicAssetUrl';
import '../styles/employee-badges.css';

const TEXT = {
  loading: '\u062c\u0627\u0631\u064a \u0627\u0644\u062a\u062d\u0642\u0642 \u0645\u0646 \u0627\u0644\u0628\u0637\u0627\u0642\u0629...',
  loadError: '\u062a\u0639\u0630\u0631 \u0627\u0644\u062a\u062d\u0642\u0642 \u0645\u0646 \u0627\u0644\u0628\u0637\u0627\u0642\u0629',
  invalidLink: '\u0631\u0627\u0628\u0637 \u0627\u0644\u062a\u062d\u0642\u0642 \u063a\u064a\u0631 \u0635\u0627\u0644\u062d \u0623\u0648 \u0645\u0646\u062a\u0647\u064a \u0627\u0644\u0635\u0644\u0627\u062d\u064a\u0629',
  invalidHelp: '\u062a\u0623\u0643\u062f \u0645\u0646 \u0645\u0633\u062d QR \u0645\u0646 \u0628\u0637\u0627\u0642\u0629 \u0635\u0627\u062f\u0631\u0629 \u0639\u0646 \u0627\u0644\u0646\u0638\u0627\u0645 \u0627\u0644\u0631\u0633\u0645\u064a \u0644\u0644\u0634\u0631\u0643\u0629.',
  officialPage: '\u0635\u0641\u062d\u0629 \u0627\u0644\u062a\u062d\u0642\u0642 \u0627\u0644\u0631\u0633\u0645\u064a\u0629',
  employeePhoto: '\u0635\u0648\u0631\u0629',
  role: '\u0627\u0644\u062f\u0648\u0631',
  departmentPhone: '\u0647\u0627\u062a\u0641 \u0627\u0644\u0645\u0634\u0631\u0641',
  phone: '\u0627\u0644\u0647\u0627\u062a\u0641',
  email: '\u0627\u0644\u0628\u0631\u064a\u062f \u0627\u0644\u0625\u0644\u0643\u062a\u0631\u0648\u0646\u064a',
  verification: '\u062d\u0627\u0644\u0629 \u0627\u0644\u0627\u0639\u062a\u0645\u0627\u062f',
  address: '\u0627\u0644\u0639\u0646\u0648\u0627\u0646',
  website: '\u0627\u0644\u0645\u0648\u0642\u0639 \u0627\u0644\u0625\u0644\u0643\u062a\u0631\u0648\u0646\u064a',
  websiteButton: '\u0641\u062a\u062d \u0645\u0648\u0642\u0639 \u0627\u0644\u0634\u0631\u0643\u0629',
  unavailable: '\u063a\u064a\u0631 \u0645\u062a\u0648\u0641\u0631',
  companyLogo: '\u0634\u0639\u0627\u0631 \u0627\u0644\u0634\u0631\u0643\u0629'
};

const BadgeVerificationPage = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [payload, setPayload] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let isMounted = true;

    const load = async () => {
      try {
        setLoading(true);
        const response = await employeeBadgeService.verifyToken(token);
        if (!isMounted) return;
        setPayload(response);
      } catch (loadError) {
        if (!isMounted) return;
        setError(loadError.response?.data?.error || TEXT.loadError);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      isMounted = false;
    };
  }, [token]);

  const verificationTheme = useMemo(() => {
    if (!payload) return 'neutral';
    return payload.is_verified ? 'success' : 'warning';
  }, [payload]);

  if (loading) {
    return (
      <Container className="badge-verification-page">
        <div className="badge-verification-loading">
          <Spinner animation="border" />
          <p>{TEXT.loading}</p>
        </div>
      </Container>
    );
  }

  if (error || !payload) {
    return (
      <Container className="badge-verification-page">
        <div className="badge-verification-shell is-invalid">
          <div className="badge-verification-badge"><FaExclamationTriangle /></div>
          <h1>{TEXT.loadError}</h1>
          <p>{error || TEXT.invalidLink}</p>
          <Alert variant="warning" className="mb-0">
            {TEXT.invalidHelp}
          </Alert>
        </div>
      </Container>
    );
  }

  const company = payload.company_identity || {};
  const user = payload.user || {};
  const badge = payload.badge || {};
  const display = payload.display || {};
  const profileImageSrc = buildPublicAssetUrl(user.profile_image_url);
  const companyAddressLine = display.company_address_line || company.company_address_line || '-';
  const displayRowsByKey = Object.fromEntries(
    (Array.isArray(display.meta_rows) ? display.meta_rows : []).map((row) => [row.key, row])
  );

  return (
    <Container className={`badge-verification-page theme-${verificationTheme}`}>
      <div className={`badge-verification-shell is-${verificationTheme}`}>
        <header className="badge-verification-head">
          {company.logo_data_uri ? (
            <img src={company.logo_data_uri} alt={TEXT.companyLogo} className="badge-verification-logo" />
          ) : null}
          <div className="badge-verification-kicker">
            <FaShieldAlt />
            <span>{TEXT.officialPage}</span>
          </div>
          <h1>{company.company_name}</h1>
          <p>{payload.official_statement}</p>
        </header>

        <section className="badge-verification-status">
          <div className={`badge-verification-status-pill is-${verificationTheme}`}>
            {payload.is_verified ? <FaCheckCircle /> : <FaExclamationTriangle />}
            <span>{payload.verification_status_label_ar}</span>
          </div>
          <p>{payload.employee_statement}</p>
        </section>

        <section className="badge-verification-profile">
          <div className="badge-verification-photo">
            {profileImageSrc ? (
              <img src={profileImageSrc} alt={`${TEXT.employeePhoto} ${user.full_name || ''}`} />
            ) : (
              <div className="badge-verification-photo__fallback">
                {(user.full_name || '?').trim().charAt(0) || '?'}
              </div>
            )}
          </div>

          <div className="badge-verification-profile__info">
            <h2>{user.full_name}</h2>
            <p>{user.job_title || user.role_label_ar}</p>

            <dl className="badge-verification-meta">
              <div>
                <dt>{TEXT.role}</dt>
                <dd>{user.role_label_ar}</dd>
              </div>
              <div>
                <dt>{TEXT.departmentPhone}</dt>
                <dd dir="ltr">{displayRowsByKey.department_phone?.value || user.department_phone || TEXT.unavailable}</dd>
              </div>
              <div>
                <dt>{TEXT.phone}</dt>
                <dd dir="ltr">{user.phone || TEXT.unavailable}</dd>
              </div>
              <div>
                <dt>{TEXT.email}</dt>
                <dd>{user.email || TEXT.unavailable}</dd>
              </div>
              <div>
                <dt>{TEXT.verification}</dt>
                <dd>{badge.badge_status_label_ar || payload.verification_status_label_ar}</dd>
              </div>
              <div>
                <dt>{TEXT.address}</dt>
                <dd>{companyAddressLine}</dd>
              </div>
              <div>
                <dt>{TEXT.website}</dt>
                <dd dir="ltr">www.ict.com.ly</dd>
              </div>
            </dl>
          </div>
        </section>

        <footer className="badge-verification-footer">
          <Button
            variant="light"
            onClick={() => window.open('https://www.ict.com.ly', '_blank', 'noopener,noreferrer')}
            disabled={false}
          >
            <FaLink />
            <span>{TEXT.websiteButton}</span>
          </Button>
        </footer>
      </div>
    </Container>
  );
};

export default BadgeVerificationPage;
